/* Copyright (c) 2020-present CloudIO Inc */

import { bingMapType } from 'componentregistry/registry/constants';
import { hasAutoLayoutParent } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { BingMapItem, defaultValue } from './BingMap';
import { page as SamplePage } from '../../help/sample_pages/BingMapPage';
import { isEmpty, isNotEmpty } from 'utils/isEmpty';
import { getName } from '../../registry/Manager';

const BingMapManager: ComponentManagerFn<'BingMap'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'BingMap'>(platform, 'BingMap'),
    type: 'BingMap',
    name: 'Bing Map',
    isChildAllowed: (page, item, childType, { pageId, appUid }) => {
      if (
        !(
          [
            'ExpressionWatchListener',
            'Popover',
            'PulseTip',
            'TriggerOnClick',
            'TriggerOnClick',
            'TriggerOnDoubleClick',
            'TriggerOnMouseEnter',
            'TriggerOnMouseLeave',
          ] as ItemType[]
        ).includes(childType)
      )
        return false;
      if (childType === 'PulseTip' || childType === 'InfoBox') {
        const children = platform.getChildrenOfType(
          appUid,
          pageId,
          page,
          item,
          childType,
        );
        return children.length === 0
          ? true
          : `${getName(childType)} already added`;
      }
      return true;
    },
    defaultValue,
    props: {
      mapType: {
        label: 'Map Type',
        type: 'select',
        options: bingMapType,
      },
      referenceLatitude: {
        label: 'Reference Latitude',
        type: 'numberInput',
      },
      referenceLongitude: {
        label: 'Reference Longitude',
        type: 'numberInput',
      },
      bingMapHeight: {
        label: 'Height',
        type: 'number',
        min: 0,
        max: 800,
        step: 40,
      },
      zoom: {
        label: 'Default Zoom',
        type: 'number',
        min: 1,
        max: 22,
        step: 1,
      },
      minZoom: {
        label: 'Min Zoom',
        type: 'number',
        min: 1,
        max: 19,
        step: 1,
      },
      maxZoom: {
        label: 'Max Zoom',
        type: 'number',
        min: 1,
        max: 19,
        step: 1,
      },
      enableHighDpi: {
        label: 'Enable High Dpi',
        type: 'boolean',
      },
      showDashboard: {
        label: 'Show Dashboard',
        type: 'boolean',
      },
      showLocateMe: {
        label: 'Show Locate Me',
        type: 'boolean',
      },
      showMapTypeSelector: {
        label: 'Show Map Type Select',
        type: 'boolean',
      },
      showZoomButtons: {
        label: 'Show Zoom Buttons',
        type: 'boolean',
      },
      liteMode: {
        label: 'Lite Mode',
        type: 'boolean',
      },
      showClusters: {
        label: 'Show Clusters',
        type: 'boolean',
      },
      showTermsLink: {
        label: 'Show Terms Link',
        type: 'boolean',
      },
      showScalebar: {
        label: 'Show Scalebar',
        type: 'boolean',
      },
      autoZoom: {
        label: 'Auto Zoom',
        type: 'boolean',
      },
      latitudeAttribute: {
        label: 'Latitude Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Decimal', 'Double'],
        preventMultiEdit: true,
        category: 'Pushpin',
      } as ViewAttributeChooserProp,
      longitudeAttribute: {
        label: 'Longitude Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['Decimal', 'Double'],
        preventMultiEdit: true,
        category: 'Pushpin',
      } as ViewAttributeChooserProp,
      valueAttribute: {
        label: 'Value Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Pushpin',
      } as ViewAttributeChooserProp,
      titleAttribute: {
        label: 'Title Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Pushpin',
      } as ViewAttributeChooserProp,
      subtitleAttribute: {
        label: 'Subtitle Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Pushpin',
      } as ViewAttributeChooserProp,
      icon: {
        label: 'Icon',
        type: 'iconselect',
        category: 'Pushpin',
        helpText: 'Select an icon for pushpin.',
      },
      hoverIcon: {
        label: 'Hover Icon',
        type: 'iconselect',
        category: 'Pushpin',
        helpText: 'Select an icon for pushpin hover.',
      },
      iconAttribute: {
        label: 'Icon Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Pushpin',
        helpText:
          'Use this to render different icon for each pushpin. Select attribute which stores icon name. eg: map-pin',
      } as ViewAttributeChooserProp,
      hoverIconAttribute: {
        label: 'Hover Icon Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        category: 'Pushpin',
        helpText:
          'Use this to render different icon for each pushpin hover. Select attribute which stores icon name. eg: map-pin',
      } as ViewAttributeChooserProp,
      imageSrc: {
        label: 'Image Src',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Attachment'],
        preventMultiEdit: true,
        category: 'Pushpin',
        helpText:
          'Value can be image src value/attachment id of image uploded to table.',
      } as ViewAttributeChooserProp,
      hoverImageSrc: {
        label: 'Hover Image Src',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Attachment'],
        preventMultiEdit: true,
        category: 'Pushpin',
        helpText:
          'Value can be image src value/attachment id of image uploded to table.',
      } as ViewAttributeChooserProp,
      iconSize: {
        label: 'Icon Size',
        type: 'number',
        min: 0,
        max: 100,
        step: 4,
        category: 'Pushpin',
      },
      color: {
        label: 'Color',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Pushpin',
        helpText:
          'Enter the hex color code or the write an expression to derive the hex color code for the push pin. e.g.. #000000 to show in black color',
      },
      hoverColor: {
        label: 'Hover Color',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Pushpin',
        helpText:
          'Enter the hex color code or the write an expression to derive the hex color code for the push pin hover. e.g.. #000000 to show in black color',
      },
      clusterColor: {
        label: 'Cluster Color',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          'Enter the hex color code or the write an expression to derive the hex color code for the cluster. e.g.. #000000 to show in black color',
      },
    },
    isPropertyVisible: (property, itemProps, context) => {
      const { appUid, pageId, itemId } = context;
      const page = platform.getPage(appUid, pageId);
      const item = itemId ? page[itemId] : undefined;
      switch (property) {
        case 'bingMapHeight': {
          return hasAutoLayoutParent(page, page[context.itemId]).result;
        }
        case 'color':
        case 'hoverColor':
        case 'iconSize': {
          const { imageSrc, hoverImageSrc } = (item as BingMapItem)?.props;
          return isEmpty(imageSrc) && isEmpty(hoverImageSrc);
        }
        case 'icon':
        case 'hoverIcon': {
          const { iconAttribute, hoverIconAttribute, imageSrc, hoverImageSrc } =
            (item as BingMapItem)?.props;
          return !(
            isNotEmpty(iconAttribute) ||
            isNotEmpty(hoverIconAttribute) ||
            isNotEmpty(imageSrc) ||
            isNotEmpty(hoverImageSrc)
          );
        }
        case 'iconAttribute':
        case 'hoverIconAttribute': {
          const { icon, hoverIcon, imageSrc, hoverImageSrc } = (
            item as BingMapItem
          )?.props;
          return !(
            isNotEmpty(icon?.iconName) ||
            isNotEmpty(hoverIcon?.iconName) ||
            isNotEmpty(imageSrc) ||
            isNotEmpty(hoverImageSrc)
          );
        }
        case 'imageSrc':
        case 'hoverImageSrc': {
          const { icon, hoverIcon, iconAttribute, hoverIconAttribute } = (
            item as BingMapItem
          )?.props;
          return !(
            isNotEmpty(iconAttribute) ||
            isNotEmpty(hoverIconAttribute) ||
            isNotEmpty(icon?.iconName) ||
            isNotEmpty(hoverIcon?.iconName)
          );
        }
        default:
          return true;
      }
    },
    getDisplayText: (item) => '',
    category: 'Maps',
    layout: 'sizeAware',
    icon: { iconName: 'map' },
    description: `**Overview**<br/>
The Bing Map component integrates Microsoft's mapping service to visualize geospatial data within your application. It provides a fully interactive map surface where you can plot data points (Pushpins) based on latitude and longitude coordinates stored in your Data Store.
Use this component for fleet tracking dashboards, store locators, or analyzing the geographic distribution of assets.

**1. Map Configuration**<br/>
You can customize the initial state and appearance of the map viewport to fit your use case.
* **Map Type:** Choose the visual style, such as **Road** (canvasLight, road) or **Satellite** (aerial).
* **Positioning:** Set **Reference Latitude** and **Reference Longitude** to define the default center point.
* **Zoom:** Control the initial magnification with **Default Zoom** (1-22). You can also constrain the user's view by setting **Min Zoom** and **Max Zoom** levels.
* **Dimensions:** The **Bing Map Height** property allows you to define a fixed height (0-800px) for consistent layout within Auto Layout containers.

**2. Data Visualization (Pushpins)**<br/>
The core feature is plotting dynamic markers ("Pushpins") directly from a data source.
* **Coordinates:** Bind **Latitude Attribute** and **Longitude Attribute** to decimal/double fields in your data store to place pins accurately.
* **Pin Info:** Bind **Title Attribute** and **Subtitle Attribute** to display textual context when a user interacts with a pin.
* **Visual Customization:** * **Icons:** Choose a static icon for all pins or bind **Icon Attribute** to dynamically change symbols based on data (e.g., "Truck" vs. "Car").
    * **Images:** Use **Image Src** to render custom images or attachments as pin markers.
    * **Colors:** Use **Color** and **Hover Color** properties to style pins based on status (e.g., Red for "Offline", Green for "Active").

**3. Clustering**<br/>
* **Show Clusters:** When enabled, the map automatically groups nearby pushpins into a single "Cluster" icon to prevent visual clutter at low zoom levels.
* **Cluster Color:** You can customize the color of these group bubbles to match your application's branding.

**4. User Controls & UI**<br/>
Toggle various on-screen controls to tailor the end-user experience:
* **Navigation:** **Show Zoom Buttons** and **Show Map Type Select** give users manual control over the view.
* **Location:** **Show Locate Me** adds a button for users to center the map on their current physical GPS location.
* **Map Info:** **Show Scalebar** and **Show Dashboard** provide additional geographic context and navigation tools.

**5. Interactivity**<br/>
* **Triggers:** Supports event triggers like **TriggerOnClick**, **TriggerOnDoubleClick**, and **TriggerOnMouseEnter**. These can open side panels, navigate to detail pages, or update global variables.
* **Overlays:** Attach **Popover** or **PulseTip** components to display rich, interactive information overlays when a user clicks or hovers over a pushpin.

**6. Best Use Cases**<br/>
* **Logistics:** Real-time tracking of delivery vehicles or shipping containers across a region.
* **Store Locators:** Displaying branch locations with contact information and status.
* **Real Estate:** Visualizing property listings with price data and high-quality satellite imagery.
* **Infrastructure Monitoring:** Mapping the physical location of utility poles, cell towers, or sensors for maintenance teams.`,
    helpRenders: [
      {
        title: 'Default Bing Map',
        description:
          'Bing Map rendered with Default Properties. Change map typ to render to render with different map types',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        height: 300,
        visibleProps: ['mapType'],
        item: {
          children: [],
          itemId: 'bingMap',
          parent: 'flexItem',
          props: {
            mapType: 'canvasLight',
            showDashboard: false,
            referenceLatitude: 37.4048,
            referenceLongitude: -121.98777,
            zoom: 11,
          },
          type: 'BingMap',
        },
      },
      {
        title: 'Pushpin Props',
        description:
          'Specify values for latitude, longitude to render the pushpins and change other props to modify the pushpin rendering',
        page: SamplePage,
        exampleId: 'e2',
        clearParentChildren: true,
        height: 300,
        visibleProps: ['icon', 'iconSize', 'color', 'showClusters'],
        item: {
          children: [],
          itemId: 'bingMap',
          parent: 'flexItem',
          props: {
            mapType: 'canvasLight',
            icon: {
              iconName: 'charging-station',
              iconType: 'light',
            },
            iconSize: 30,
            latitudeAttribute: 'latitude',
            longitudeAttribute: 'longitude',
            titleAttribute: 'city',
            showClusters: false,
            referenceLatitude: 45,
            referenceLongitude: -86,
            showDashboard: false,
            zoom: 4,
          },
          type: 'BingMap',
        },
      },
      {
        title: 'Center',
        description:
          'Specify values for these props to change the center of the map. Eg: referenceLatitude: 45, referenceLongitude: -86',
        page: SamplePage,
        exampleId: 'e3',
        clearParentChildren: true,
        height: 300,
        visibleProps: ['referenceLatitude', 'referenceLongitude'],
        item: {
          children: [],
          itemId: 'bingMap',
          parent: 'flexItem',
          props: {
            mapType: 'canvasLight',
            iconSize: 30,
            latitudeAttribute: 'latitude',
            longitudeAttribute: 'longitude',
            titleAttribute: 'city',
            referenceLatitude: 37.4048,
            referenceLongitude: -121.98777,
            showDashboard: false,
            zoom: 5,
          },
          type: 'BingMap',
        },
      },
      {
        title: 'Zoom',
        description:
          'Specify the zoom properties to decide the zoom level of Bing Map',
        page: SamplePage,
        exampleId: 'e4',
        clearParentChildren: true,
        height: 300,
        visibleProps: ['zoom', 'maxZoom', 'minZoom'],
        item: {
          children: [],
          itemId: 'bingMap',
          parent: 'flexItem',
          props: {
            mapType: 'canvasLight',
            zoom: 11,
            minZoom: 1,
            maxZoom: 19,
            latitudeAttribute: 'latitude',
            longitudeAttribute: 'longitude',
            titleAttribute: 'city',
            referenceLatitude: 37.4048,
            referenceLongitude: -121.98777,
            showDashboard: true,
            showZoomButtons: true,
            showLocateMe: false,
            showMapTypeSelector: false,
          },
          type: 'BingMap',
        },
      },
      {
        title: 'Map Configurations',
        description: 'Change theese properties to set the map configurations',
        page: SamplePage,
        exampleId: 'e5',
        clearParentChildren: true,
        height: 300,
        visibleProps: [
          'enableHighDpi',
          'liteMode',
          'showDashboard',
          'showTermsLink',
          'showLocateMe',
          'showMapTypeSelector',
          'showZoomButtons',
        ],
        item: {
          children: [],
          itemId: 'bingMap',
          parent: 'flexItem',
          props: {
            mapType: 'canvasLight',
            enableHighDpi: true,
            liteMode: false,
            showDashboard: true,
            latitudeAttribute: 'latitude',
            longitudeAttribute: 'longitude',
            showTermsLink: false,
            titleAttribute: 'city',
            referenceLatitude: 37.4048,
            referenceLongitude: -121.98777,
            zoom: 10,
          },
          type: 'BingMap',
        },
      },
    ],
  };
};

export default BingMapManager;
